import React from "react";
import StandalonePageLayout from "../layout/StandalonePageLayout";
import {
    usePrintReady,
    DocOpening,
    DocSection,
    SubHead,
    P,
    T,
    Steps,
    List,
    Note,
    MarginNote,
    MailLink,
    Colophon,
} from "./DocParts";

function UserGuide() {
    usePrintReady();

    return (
        <StandalonePageLayout>
            <DocOpening
                appendix="B"
                label="User guide"
                title="JPRO User Guide"
                intro="Complete guide to using JPRO"
            />

            <DocSection number="B.1" label="Getting started" title="Getting Started">
                <SubHead>Creating Your Account</SubHead>
                <P>Sign up using any of these options:</P>
                <List
                    items={[
                        <><T>Email:</T> Sign up with your email and a password</>,
                        <><T>Google:</T> Sign in with your Google account</>,
                        <><T>Microsoft:</T> Sign in with your Microsoft account</>,
                    ]}
                />

                <SubHead>Initial Setup</SubHead>
                <Steps
                    items={[
                        "Complete your profile (name, phone, location)",
                        "Upload an avatar (optional)",
                        "Choose your theme (Dark/Light mode) in Settings",
                    ]}
                />
            </DocSection>

            <DocSection number="B.2" label="Dashboard" title="Dashboard Overview">
                <P>Your central hub for accessing all JPRO features:</P>
                <List
                    items={[
                        <><T>Chat:</T> Ask questions, upload documents, and get guidance from JPRO</>,
                        <><T>Projects:</T> Organize chats and documents by property, client, or topic</>,
                        <><T>Studio:</T> Generate images from text prompts</>,
                        <><T>JPRO Analyzer:</T> Property risk and profitability analysis</>,
                        <><T>Training:</T> Buy Zeffert classes, enroll your team, and track what you own</>,
                        <><T>Documents:</T> Browse all your uploaded files and generated content</>,
                        <><T>Chat History:</T> Resume or review past conversations</>,
                    ]}
                />
            </DocSection>

            <DocSection
                number="B.3"
                label="Chat"
                title="Chat"
                aside={<MarginNote>Use Deep mode when you need JPRO to think through a complex scenario.</MarginNote>}
            >
                <SubHead>What You Can Do</SubHead>
                <List
                    items={[
                        <><T>Ask Questions:</T> Get guidance on compliance, regulations, property management, and general business topics.</>,
                        <><T>Upload Documents:</T> Upload PDF, DOCX, or XLSX files and ask JPRO questions about them.</>,
                        <><T>YouTube Videos:</T> Paste a YouTube URL and discuss the video content with JPRO.</>,
                        <><T>Voice Input:</T> Click the microphone icon to speak your questions instead of typing.</>,
                        <><T>Listen to Responses:</T> Use text-to-speech to have JPRO read answers aloud with multiple voice options.</>,
                    ]}
                />

                <SubHead>Reasoning Modes</SubHead>
                <P>Toggle between two modes depending on what you need:</P>
                <List
                    items={[
                        <><T>Fast:</T> Quick answers for straightforward questions</>,
                        <><T>Deep:</T> More thorough analysis for complex compliance or regulatory questions</>,
                    ]}
                />

                <SubHead>Sharing a Chat</SubHead>
                <List
                    items={[
                        <>Click the chat menu (<T>&#8942;</T>) to access sharing options</>,
                        <><T>Export as PDF</T> — download the conversation as a document</>,
                        <><T>Copy to Clipboard</T> — paste into an email or report</>,
                        <><T>Add to Project</T> — move a chat into a project so your team can see it</>,
                    ]}
                />

                <Note label="Tips">
                    <List
                        items={[
                            "Be specific in your questions for better responses",
                            "Upload relevant documents before asking questions about them",
                            "Use Deep mode when you need JPRO to think through a complex scenario",
                        ]}
                    />
                </Note>
            </DocSection>

            <DocSection
                number="B.4"
                label="Projects"
                title="Projects"
                aside={<MarginNote>Invite links expire after 7 days.</MarginNote>}
            >
                <P>
                    Projects let you organize related chats and documents in one place. JPRO remembers what was
                    discussed across all chats in a project, so you never have to repeat yourself.
                </P>

                <SubHead>Creating a Project</SubHead>
                <List
                    items={[
                        <>Click <T>&quot;New Project&quot;</T> and give it a name, description, and color</>,
                        "Start new chats directly from within the project",
                        <>Add existing chats to the project using the chat menu (<T>&#8942;</T>)</>,
                    ]}
                />

                <SubHead>Project Documents</SubHead>
                <P>
                    Upload documents to a project and JPRO will reference them in every chat within that project.
                    No need to re-upload the same file each time.
                </P>
                <List
                    items={[
                        "Supported formats: PDF, DOCX, XLSX, CSV",
                        "Upload from the project detail page",
                        "All chats in the project can reference these documents",
                    ]}
                />

                <SubHead>Inviting Team Members</SubHead>
                <P>Collaborate on a project by inviting others:</P>
                <Steps
                    items={[
                        <>Open a project and click <T>&quot;Invite&quot;</T></>,
                        "Send an invitation by email or generate a shareable link",
                        "Choose a role for the person you're inviting",
                    ]}
                />

                <SubHead>Roles</SubHead>
                <List
                    items={[
                        <><T>Owner:</T> Full control — the person who created the project</>,
                        <><T>Editor:</T> Can chat, edit, and invite others</>,
                        <><T>Member:</T> Can chat and edit within the project</>,
                        <><T>Viewer:</T> Can see chats and documents (read-only)</>,
                    ]}
                />
                <Note>
                    Invite links expire after 7 days. You can view and manage members from the &quot;Members&quot;
                    button in the project.
                </Note>

                <SubHead>Managing Projects</SubHead>
                <List
                    items={[
                        "Pin important projects so they appear at the top of your list",
                        "Archive completed projects to keep your workspace clean",
                        "Use colors to visually organize different project types",
                        "Move chats between projects as needed",
                    ]}
                />

                <SubHead>Team Chat</SubHead>
                <P>Communicate with your team without leaving your project:</P>
                <List
                    items={[
                        <><T>From a project:</T> Click the <T>&quot;Team Chat&quot;</T> button in the project header to open the team chat drawer</>,
                        <><T>From a chat:</T> Click the blue chat bubble button in the bottom-right corner while in any project conversation</>,
                        "The drawer slides in from the right and shows your team conversations",
                        "Select a conversation to read and send messages, with full real-time support (typing indicators, reactions, file sharing)",
                        "Click the expand icon to open team chat as a full page",
                        "Click the X or the backdrop to close the drawer and return to your work",
                    ]}
                />
            </DocSection>

            <DocSection number="B.5" label="Studio" title="Studio (Image Generation)">
                <SubHead>Creative Studio</SubHead>
                <P>Generate images from a text prompt, then refine them through conversation:</P>
                <Steps
                    items={[
                        "Describe the image you want",
                        "Set aspect ratio, resolution and variant count in Settings",
                        "Send the prompt, then pick the variant you like",
                        "Keep going: describe what to change and JPRO adjusts the image",
                    ]}
                />
                <P className="mt-6">
                    Attach reference images or documents (PDF, Word, Excel) with the + button to steer the result.
                    Bookmark a result to keep it in your history.
                </P>

                <SubHead>Prompt Tips</SubHead>
                <List
                    items={[
                        'Include style keywords: "photorealistic", "watercolor", "3D render"',
                        'Specify lighting: "sunset", "soft morning light", "dramatic"',
                        'Describe composition: "close-up", "wide angle", "aerial view"',
                        "Add details: colors, textures, mood",
                    ]}
                />
            </DocSection>

            <DocSection number="B.6" label="Analyzer" title="JPRO Analyzer">
                <P>Analyze property risk and profitability with a comprehensive JPRO Index score.</P>

                <SubHead>How to Use</SubHead>
                <List
                    items={[
                        <><T>Upload a report:</T> Upload an inspection report (PDF or DOCX) and JPRO will extract the data automatically</>,
                        <><T>Enter manually:</T> Fill in property, inspection, financial, and operations data yourself</>,
                        <><T>Market lookup:</T> JPRO can fetch comparable market data based on the property address</>,
                    ]}
                />

                <SubHead>JPRO Index Score</SubHead>
                <P>The JPRO Index rates properties from 0-100 across profitability and affordability:</P>
                <List
                    items={[
                        <><T>90-100 (Highest):</T> Strong alignment of profitability and affordability</>,
                        <><T>70-89 (Watch):</T> Stable but early warning signs present</>,
                        <><T>60-69 (Imminent):</T> Pressures likely to impact performance soon</>,
                        <><T>Below 60 (Critical):</T> Urgent risks requiring immediate action</>,
                    ]}
                />

                <SubHead>Reports</SubHead>
                <List
                    items={[
                        "Download your analysis as a PDF or Word document",
                        "Reports include scores, risk breakdown, market comparison, and recommendations",
                    ]}
                />
            </DocSection>

            {/* Training. This is a public page, so it covers only the client
                experience: buying, enrolling, and taking a class. Content
                development, internal pricing, and the review process are
                internal-only and documented for staff elsewhere. */}
            <DocSection
                number="B.7"
                label="Training"
                title="Training"
                aside={<MarginNote>The development fee is charged once per class, never again.</MarginNote>}
            >
                <P>
                    Buy Zeffert training for your team, get them enrolled, and keep track of what you own. What you
                    see under Training depends on your account: clients get the buying and enrollment view described
                    here.
                </P>

                <SubHead>Finding a class</SubHead>
                <List
                    items={[
                        <><T>Offered Classes</T> lists what is available, with level, delivery (eLearning or live), length, and price per seat</>,
                        <><T>Request a Class</T> if what you need is not listed. Describe who it is for and what they should be able to do afterwards. A request costs nothing and is not a purchase</>,
                    ]}
                />

                <SubHead>Buying</SubHead>
                <P>
                    Two charges can appear: a price <T>per seat</T>, and a one-time <T>development fee</T> that is
                    charged once per class for your organization and never again. Adding seats later or renewing
                    costs seats only. Payment is by card, and you get an itemized receipt.
                </P>

                <SubHead>Enrolling your team</SubHead>
                <List
                    items={[
                        "Invite people by email. Paste a whole list at once, including a column copied from a spreadsheet",
                        <><T>Reassign</T> a seat to someone else without spending another seat</>,
                        <><T>Resend</T> an invitation whose link has lapsed</>,
                    ]}
                />

                <SubHead>Taking the class and tracking it</SubHead>
                <List
                    items={[
                        <><T>Start the class</T> opens the learning system. It appears while the class is paid and inside its 365-day validity</>,
                        <><T>My Classes</T> shows seats used, seats remaining, and the expiry date</>,
                        <><T>Orders</T> is your billing history, with receipts and a Buy again action. An expired class can be renewed, and a live one can have seats added</>,
                    ]}
                />
            </DocSection>

            <DocSection number="B.8" label="Documents" title="Documents">
                <P>All your uploaded files and generated content in one place:</P>
                <List
                    items={[
                        "View all uploaded documents and generated images",
                        "Filter by type: PDFs, Images, Documents",
                        "Search by title",
                        "Download, preview, or delete files",
                        "Supported formats: PDF, DOCX, XLSX, PNG, JPG",
                    ]}
                />
            </DocSection>

            <DocSection number="B.9" label="Troubleshooting" title="Troubleshooting">
                <SubHead>Chat &amp; File Upload</SubHead>
                <List
                    items={[
                        <><T>Chat not loading:</T> Refresh the page or clear your browser cache</>,
                        <><T>File won&apos;t upload:</T> Check that it&apos;s a supported format and under 50MB</>,
                        <><T>Session expired:</T> Just log in again to continue where you left off</>,
                    ]}
                />

                <SubHead>Analyzer</SubHead>
                <List
                    items={[
                        <><T>Extraction not working:</T> Make sure the document has readable text (not scanned images)</>,
                        <><T>Missing market data:</T> Double-check the property address</>,
                        <><T>Report won&apos;t download:</T> Check your browser&apos;s popup settings</>,
                    ]}
                />

                <SubHead>Login &amp; Account</SubHead>
                <List
                    items={[
                        <><T>Forgot password:</T> Use the &quot;Forgot Password&quot; link on the login page</>,
                        <><T>Google/Microsoft login not working:</T> Make sure your browser allows popups</>,
                    ]}
                />
            </DocSection>

            <DocSection number="B.10" label="Support" title="Need Help?">
                <List
                    items={[
                        <>Email: <MailLink /></>,
                        "Response time: Within 2 business days",
                        "Include screenshots and error messages for faster resolution",
                    ]}
                />
            </DocSection>

            <Colophon>&copy; 2025 Zeffert &amp; Associates. All rights reserved.</Colophon>
        </StandalonePageLayout>
    );
}

export default UserGuide;
